import { useEffect, useRef } from 'react';
import { useFormState } from 'react-hook-form';
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

interface UseRevalidateFieldOnDependencyChangeOptions<TFieldValues extends FieldValues> {
  dependencies: readonly unknown[];
  fieldName: FieldPath<TFieldValues>;
  form: UseFormReturn<TFieldValues>;
}

const useRevalidateFieldOnDependencyChange = <TFieldValues extends FieldValues>({
  dependencies,
  fieldName,
  form,
}: UseRevalidateFieldOnDependencyChangeOptions<TFieldValues>) => {
  const previousDependenciesRef = useRef(dependencies);
  const formState = useFormState({
    control: form.control,
    name: fieldName,
  });
  const { isDirty, isTouched } = form.getFieldState(fieldName, formState);

  // Revalidate only when dependency values actually change; callers pass inline tuples, so array identity alone is too noisy here.
  useEffect(() => {
    const dependenciesDidChange = (
      previousDependenciesRef.current.length !== dependencies.length
      || dependencies.some((dependency, index) => previousDependenciesRef.current[index] !== dependency)
    );
    previousDependenciesRef.current = dependencies;

    if (!dependenciesDidChange) {
      return;
    }

    const fieldValue = form.getValues(fieldName);
    let hasValue = Boolean(fieldValue);

    if (typeof fieldValue === 'string') {
      hasValue = fieldValue.trim().length > 0;
    } else if (Array.isArray(fieldValue)) {
      hasValue = fieldValue.length > 0;
    }

    if (!isTouched && !isDirty && !hasValue) {
      return;
    }

    // Dependency changes should keep already-interacted fields honest without surfacing validation on untouched empty fields.
    const revalidateField = async () => {
      await form.trigger(fieldName);
    };

    revalidateField();
  }, [dependencies, fieldName, form, isDirty, isTouched]);
};

export default useRevalidateFieldOnDependencyChange;
